import { inject, Injectable } from '@angular/core';
import { Board } from '../../../../models/configurators-2d/applications/fence/board';
import { forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import { PostType } from '../../../../models/configurators-2d/applications/fence/post-type';
import { Decoration } from '../../../../models/configurators-2d/applications/fence/decoration';
import { Door } from '../../../../models/configurators-2d/applications/fence/door';
import { InstallationStateService } from './installation.state.service';
import { BoardStateService } from './board.state.service';
import { ColorStateService } from './color.state.service';
import { PostTypeStateService } from './post-type.state.service';
import { DecorationStateService } from './decoration.state.service';
import { DoorStateService } from './door.state.service';
import { FenceService } from '../../../../services/configurators-2d/applications/fence.service';
import { InstallationMixingStateService } from './installation-mixing.state.service';

@Injectable({
  providedIn: 'root',
})
export class DataLoaderService {
  private readonly installationStateService = inject(InstallationStateService);
  private readonly installationMixingStateService = inject(InstallationMixingStateService);
  private readonly boardStateService = inject(BoardStateService);
  private readonly colorStateService = inject(ColorStateService);
  private readonly postTypeStateService = inject(PostTypeStateService);
  private readonly decorationStateService = inject(DecorationStateService);
  private readonly doorStateService = inject(DoorStateService);
  private readonly fenceService = inject(FenceService);

  /**
   * Dans le cas du chargement d'une config, il faut envoyer les requêtes pour récupérer les données des poses, des lames, des décors et des portillons
   */
  initData(): Observable<Map<number, Board[]> | PostType[] | Decoration[] | Door[]> {
    const configuration = this.fenceService.configuration;

    // pas de chargement de données si aucune pose n'est sélectionné
    if (configuration.configurationInstallations.length === 0) {
      return of([]);
    }

    return forkJoin([
      this.installationStateService.setList(),
      this.installationMixingStateService.setList(),
    ]).pipe(
      switchMap(() => {
        // chargement des types de lame si on a au moins une de sélectionnée
        if (!configuration.configurationInstallations.some(i => i.configurationBoards.some(b => b.fenceTypeId))) {
          return of([]);
        }

        const requests: Observable<Map<number, Board[]> | PostType[] | Decoration[] | Door[]>[] =
          [
            this.boardStateService.setList(configuration),
            this.postTypeStateService.setList(configuration),
            this.decorationStateService.setList(configuration),
          ];

        if (configuration.sides.some(s => s.sections.some(section => section.hasDoor))) {
          requests.push(this.doorStateService.setList(configuration));
        }

        // Récupération des lames, décorations, panneaux et portillons
        return forkJoin(requests).pipe(tap(() => this.colorStateService.setList(configuration)));
      })
    );
  }
}
